import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import serviceImg from "../components/Firefly_Gemini Flash (1).png";

// Exact structured services from your uploaded image screens
const serviceDataMap = {
  "photo-video-editing": {
    mainTitle: "Real Estate Photo & Video Editing",
    subtext: "Premium real estate post-production, multi-exposure blending, and cinematic video storytelling.",
    sections: [
      {
        heading: "Real Estate Photo Editing",
        items: [
          "Virtual Staging",
          "HDR Photos",
          "Day-to-Dusk Conversion (Twilight)",
          "Interior Colour Balancing",
          "Sky Replacement",
          "Perspective Correction",
          "Furniture Clean-up",
          "Amenity Highlights Editing (Item Removal)"
        ]
      },
      {
        heading: "Real Estate Video Editing",
        items: [
          "Walkthrough Video Editing",
          "Cinematic Real Estate Editing",
          "Social Media / Reel Editing",
          "Promotional / Advertisement Editing",
          "Map & Location Highlight Editing",
          "Agent Presentation / Talking Head Editing",
          "Drone Footage Editing"
        ]
      }
    ]
  },
  "photography": {
    mainTitle: "Photography & Videography",
    subtext: "Professional high-end field shoots, premium creative framing, and crisp studio setups.",
    sections: [
      {
        heading: "Photography",
        items: [
          "Architectural Photography",
          "Portrait Photography",
          "Fashion Photography",
          "Food Photography",
          "Wedding Photography",
          "Product Photography",
          "Event Photography"
        ]
      },
      {
        heading: "Videography",
        items: [
          "Real Estate Videography",
          "Cinematic / Film Videography",
          "Wedding Videography",
          "Commercial / Advertisement Videography",
          "Social Media Videography",
          "Corporate Videography",
          "Event Videography",
          "Dronography"
        ]
      }
    ]
  },
  "graphic-design-real-estate": {
    mainTitle: "Graphic Design (Marketing & Advertising Design)",
    subtext: "Creative corporate branding layouts, high-impact marketing assets, and modern dynamic animations.",
    sections: [
      {
        heading: "Graphic Design (Marketing & Advertising Design)",
        items: [
          "Posters",
          "Banners",
          "social media ads",
          "Brochures",
          "Flyers Packaging Design",
          "Product packaging",
          "Boxes, labels, wrappers"
        ]
      },
      {
        heading: "Clipping Path Photo Editing Services",
        items: [
          "Image Background Removal",
          "Professional Neck Joint",
          "Clipping Path for E-commerce",
          "Clipping Path with Natural Shadows"
        ]
      },
      {
        heading: "Motion Graphics Design",
        items: [
          "Animated graphics",
          "Videos, animations, GIFs",
          "Commercial / Advertisement Editing",
          "Social Media Editing",
          "Documentary Editing",
          "Music Video Editing",
          "Corporate Video Editing",
          "Motion Graphics & VFX Editing",
          "Educational / Tutorial Editing"
        ]
      }
    ]
  }
};

const features = [
  { title: "Expert Visual Enhancements" },
  { title: "Fast Turnaround Delivery" },
  { title: "Dedicated Photo Editing Team" },
  { title: "Consistent Quality Output" },
  { title: "Scalable & Flexible Support" },
  { title: "Cost-Effective Solutions" }
];

export default function ServicesPage() {
  const navigate = useNavigate();
  const { type } = useParams();

  const currentData = serviceDataMap[type] || serviceDataMap["photo-video-editing"];

  const [showForm, setShowForm] = React.useState(false);
  const [step, setStep] = React.useState(1);

  const [formData, setFormData] = React.useState({
    service: currentData.mainTitle,
    turnaround: "",
    name: "",
    email: "",
    phone: "",
    requirements: ""
  });

  React.useEffect(() => {
    setFormData((prev) => ({ ...prev, service: currentData.mainTitle }));
  }, [currentData]);

  const handleWhatsApp = () => {
    const phoneNumber = "919487177305";
    const message = `
🔥 NEW QUOTE REQUEST
📸 Service: ${formData.service}
⏱ Turnaround: ${formData.turnaround}
👤 Name: ${formData.name}
📧 Email: ${formData.email}
📱 Phone: ${formData.phone}
📝 Requirements: ${formData.requirements}
`;
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  return (
    <div style={styles.container}>
      {/* HERO SECTION */}
      <div style={styles.hero} className="responsive-hero">
        <div style={styles.goldGlowAmbient}></div>
        <h1 style={styles.title}>{currentData.mainTitle}</h1>
        <p style={styles.subtitle}>{currentData.subtext}</p>
        <div style={styles.heroDivider}></div>
      </div>

      {/* DYNAMIC LIST CORES */}
      <div style={styles.section} className="responsive-section">
        <div style={styles.contentLayout} className="responsive-layout-gap">
          {currentData.sections.map((sec, idx) => (
            <div key={idx} style={styles.contentBlock}>
              <div style={styles.headerFlex} className="responsive-header-flex">
                <h3 style={styles.blockTitle}>{sec.heading}</h3>
                <div style={styles.blockLine}></div>
              </div>
              
              <div style={styles.listGrid} className="responsive-grid">
                {sec.items.map((item, i) => (
                  <div key={i} className="luxury-list-row" style={styles.listItem}>
                    <span className="gold-indicator" style={styles.goldBulletBar}></span>
                    <span style={styles.itemText}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* VALUE PROPOSITION SECTION */}
      <div style={styles.whyWrapper} className="responsive-why-wrapper">
        <div style={styles.whyContentInner} className="responsive-flex-column">
          
          <div style={styles.whyLeftColumn} className="responsive-w100">
            <span style={styles.smallTitle}>VALUE PROPOSITION</span>
            <h3 style={styles.whyTitle}>
              Why Outsource Real Estate Photo & Video Editing Services to Us
            </h3>
            <p style={styles.whyDesc}>
              Outsource your real estate photo editing to us for high-quality visuals.
            </p>

            <div style={styles.timelineStack}>
              {features.map((f, i) => (
                <div key={i} className="timeline-row" style={styles.timelineItem}>
                  <div style={styles.timelineNode}>
                    <div className="node-dot" style={styles.nodeDotInside}></div>
                  </div>
                  <div style={styles.timelineTextPane}>
                    <h4 style={styles.featureItemTitle}>{f.title}</h4>
                    <p style={styles.featureItemDesc}>Professional editing with best quality output.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.whyRightColumn} className="responsive-image-container">
            <div style={styles.imageFadeOverlay} className="responsive-fade-overlay"></div>
            <img src={serviceImg} alt="service" style={styles.fadedSideImage} />
          </div>

        </div>
      </div>

      {/* CTA INTERFACE */}
      <div style={styles.ctaWrapper} className="responsive-section">
        <div style={styles.ctaCleanBar} className="responsive-cta-bar">
          <div style={styles.icon}>OFFICIAL PARTNER</div>
          <div style={{ flex: 1 }}>
            <p style={styles.ctaText}>
              You can also contact us for any service or for a{" "}
              <span style={styles.link}>custom package</span>.
            </p>
            <div style={styles.badges} className="responsive-badges">
              <span style={styles.badgeGold}>⚡ No Obligation Quotes</span>
              <span style={styles.badgeDark}>⏱ Within 24 Hours</span>
            </div>
          </div>
        </div>

        <div style={styles.buttons} className="responsive-btn-stack">
          <button style={styles.backBtn} onClick={() => navigate("/services")}>
            ← Back to Services
          </button>
          <button style={styles.mainBtn} onClick={() => { setShowForm(true); setStep(1); }}>
            Request A Quote Now ✨
          </button>
        </div>
      </div>

      {/* MODAL POPUP BUILDER - NOW FULLY COMPACT FOR MOBILE */}
      {showForm && (
        <div style={styles.overlay} className="responsive-overlay">
          <div style={styles.quoteModal} className="responsive-modal">
            <button style={styles.closeBtn} onClick={() => { setShowForm(false); setStep(1); }}>✕</button>

            <div>
              <span style={styles.stepBadge}>STEP {step} OF 3</span>
              <h2 style={styles.modalTitle} className="responsive-modal-title">Custom Quote Builder</h2>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: step === 1 ? "33%" : step === 2 ? "66%" : "100%" }} />
              </div>
            </div>

            {step === 1 && (
              <div>
                <h3 style={styles.question} className="responsive-modal-q">What core service are you looking for?</h3>
                <div style={styles.modalOptionStack}>
                  {[
                    { title: "Photography" },
                    { title: "Videography & Video Editing" },
                    { title: "Graphic Design & Motion Graphics" }
                  ].map((item, i) => (
                    <button
                      key={i}
                      className="modal-interactive-row"
                      style={styles.modalRowOption}
                      onClick={() => { setFormData({ ...formData, service: item.title }); setStep(2); }}
                    >
                      <span className="row-arrow" style={styles.rowArrow}>→</span>
                      <span style={styles.optionText} className="responsive-option-text">{item.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3 style={styles.question} className="responsive-modal-q">What is your expected turnaround time?</h3>
                <div style={styles.modalOptionStack}>
                  {[
                    { title: "Urgent (Within 24 Hours)" },
                    { title: "Standard (2 - 3 Days)" }
                  ].map((item, i) => (
                    <button
                      key={i}
                      className="modal-interactive-row"
                      style={styles.modalRowOption}
                      onClick={() => { setFormData({ ...formData, turnaround: item.title }); setStep(3); }}
                    >
                      <span className="row-arrow" style={styles.rowArrow}>→</span>
                      <span style={styles.optionText} className="responsive-option-text">{item.title}</span>
                    </button>
                  ))}
                </div>
                <button style={styles.backLink} onClick={() => setStep(1)}>
                  ← Go back to Step 1
                </button>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3 style={styles.question} className="responsive-modal-q">Almost done! Send details directly via WhatsApp:</h3>
                <input
                  style={styles.modalInput}
                  className="responsive-input"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <input
                  style={styles.modalInput}
                  className="responsive-input"
                  placeholder="Your Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <input
                  style={styles.modalInput}
                  className="responsive-input"
                  placeholder="Your Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <textarea
                  rows="3"
                  style={{ ...styles.modalInput, resize: "none" }}
                  className="responsive-input"
                  placeholder="Any specific requirements?"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                />
                <div style={styles.footerBtns} className="responsive-footer-btns">
                  <button style={styles.backLink} onClick={() => setStep(2)}>
                    ← Back
                  </button>
                  <button style={styles.whatsappBtn} className="responsive-wa-btn" onClick={handleWhatsApp}>
                    Send WhatsApp 🚀
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS HOVERS & MOBILE RESPONSIVE OPTIMIZATIONS */}
      <style>{`
        .luxury-list-row {
          position: relative;
          transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .luxury-list-row:hover {
          padding-left: 12px;
          background: linear-gradient(90deg, rgba(212, 175, 55, 0.04) 0%, rgba(255,255,255,0) 100%);
          border-bottom: 1px solid #d4af37 !important;
        }
        .luxury-list-row:hover .gold-indicator {
          background: #d4af37 !important;
          height: 100% !important;
          top: 0 !important;
        }

        .timeline-row {
          transition: all 0.3s ease;
        }
        .timeline-row:hover {
          padding-left: 6px;
        }
        .timeline-row:hover .node-dot {
          background: #d4af37 !important;
          box-shadow: 0 0 12px #d4af37;
          transform: scale(1.3);
        }

        .modal-interactive-row {
          transition: all 0.25s ease;
        }
        .modal-interactive-row:hover {
          background: #fdfbf7 !important;
          padding-left: 16px !important;
          border-left: 3px solid #d4af37 !important;
        }
        .modal-interactive-row:hover .row-arrow {
          color: #d4af37 !important;
          opacity: 1 !important;
          transform: translateX(4px);
        }

        /* 📱 TABLET VIEW */
        @media (max-width: 991px) {
          .responsive-flex-column { flex-direction: column !important; align-items: flex-start !important; gap: 40px !important; }
          .responsive-w100 { width: 100% !important; flex: 1 1 100% !important; }
          .responsive-image-container { width: 100% !important; flex: 1 1 100% !important; height: 360px !important; justify-content: center !important; }
          .responsive-fade-overlay { background: linear-gradient(to top, #ffffff 0%, rgba(255, 255, 255, 0.8) 20%, rgba(255, 255, 255, 0) 100%) !important; }
          .responsive-why-wrapper { padding: 60px 20px !important; }
        }

        /* 📱 MOBILE, ANDROID & IPHONE LAYOUT OPTIMIZATIONS */
        @media (max-width: 768px) {
          .responsive-hero { padding: 80px 20px 60px !important; }
          .responsive-section { padding: 40px 20px !important; }
          .responsive-layout-gap { gap: 50px !important; }
          .responsive-header-flex { margin-bottom: 24px !important; }
          .responsive-grid { grid-template-columns: 1fr !important; row-gap: 16px !important; }
          
          .responsive-btn-stack {
            flex-direction: column-reverse !important;
            width: 100% !important;
            gap: 12px !important;
            margin-top: 35px !important;
          }
          .responsive-btn-stack button { width: 100% !important; text-align: center !important; padding: 14px 24px !important; }
          .responsive-cta-bar { gap: 16px !important; padding: 30px 0 !important; }
          .responsive-badges { gap: 8px !important; }

          /* 🔥 COMPACT MODAL BUILDER FOR MOBILE SCREENS */
          .responsive-overlay { padding: 8px !important; }
          .responsive-modal {
            padding: 24px 16px !important; 
            width: 95% !important;
            max-width: 380px !important; /* Made significantly narrower for smaller mobile/iPhone view */
            border-radius: 8px !important;
          }
          .responsive-modal-title { fontSize: 19px !important; marginBottom: 12px !important; }
          .responsive-modal-q { fontSize: 15px !important; marginBottom: 16px !important; }
          
          .modal-interactive-row { padding: 12px 6px !important; }
          .responsive-option-text { fontSize: 13.5px !important; }
          
          .responsive-input {
            padding: 10px 12px !important;
            marginBottom: 10px !important;
            fontSize: 13.5px !important;
          }
          .responsive-footer-btns { marginTop: 5px !important; }
          .responsive-wa-btn { padding: 11px 20px !important; fontSize: 13.5px !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "Inter, sans-serif",
    background: "#ffffff",
    width: "100%",
    overflowX: "hidden"
  },
  hero: {
    background: "#fdfbf7", 
    textAlign: "center",
    padding: "110px 40px 80px",
    position: "relative",
    overflow: "hidden",
    borderBottom: "1px solid #f1f5f9"
  },
  goldGlowAmbient: {
    position: "absolute",
    top: "-30%",
    left: "50%",
    transform: "translate(-50%, 0)",
    width: "700px",
    height: "300px",
    background: "rgba(212, 175, 55, 0.12)", 
    filter: "blur(120px)",
    pointerEvents: "none"
  },
  title: {
    fontSize: "clamp(26px, 4.5vw, 44px)",
    fontWeight: "800",
    lineHeight: "1.25",
    color: "#0b0f19", 
    maxWidth: "1000px",
    margin: "0 auto",
    letterSpacing: "-0.5px"
  },
  subtitle: {
    color: "#475569", 
    maxWidth: "750px",
    margin: "20px auto 0",
    lineHeight: "1.7",
    fontSize: "16px",
    fontWeight: "500"
  },
  heroDivider: {
    width: "100px",
    height: "3px",
    background: "linear-gradient(90deg, #b38728, #fbf5b7, #daaf38)",
    margin: "35px auto 0",
    borderRadius: "2px"
  },
  section: {
    padding: "60px 40px"
  },
  contentLayout: {
    display: "flex",
    flexDirection: "column",
    gap: "80px",
    maxWidth: "1280px",
    margin: "0 auto"
  },
  contentBlock: {
    width: "100%"
  },
  headerFlex: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    marginBottom: "40px"
  },
  blockTitle: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#0b0f19",
    letterSpacing: "-0.5px",
    margin: 0,
    whiteSpace: "nowrap"
  },
  blockLine: {
    flex: 1,
    height: "1px",
    background: "#e2e8f0"
  },
  listGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    rowGap: "26px",
    columnGap: "50px",
    width: "100%"
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    borderBottom: "1px solid #f1f5f9",
    paddingBottom: "16px",
    cursor: "default"
  },
  goldBulletBar: {
    width: "3px",
    height: "14px",
    background: "#cbd5e1",
    transition: "all 0.3s ease",
    flexShrink: 0
  },
  itemText: {
    fontSize: "15px",
    fontWeight: "500",
    color: "#334155",
    lineHeight: "1.4"
  },
  whyWrapper: {
    width: "100%",
    background: "#ffffff",
    padding: "80px 0 80px 40px",
    overflow: "hidden"
  },
  whyContentInner: {
    maxWidth: "1280px",
    margin: "0 0 0 auto",
    display: "flex",
    alignItems: "center",
    gap: "40px"
  },
  whyLeftColumn: {
    flex: "1 1 50%",
    zIndex: 2,
    position: "relative"
  },
  whyRightColumn: {
    flex: "1 1 50%",
    position: "relative",
    height: "520px",
    display: "flex",
    justifyContent: "flex-end"
  },
  fadedSideImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  imageFadeOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to right, #ffffff 0%, rgba(255, 255, 255, 0.9) 15%, rgba(255, 255, 255, 0) 100%)",
    zIndex: 1
  },
  smallTitle: {
    color: "#d4af37",
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "2.5px"
  },
  whyTitle: {
    margin: "14px 0 8px",
    fontSize: "28px",
    lineHeight: "1.3",
    fontWeight: "800",
    color: "#0b0f19",
    letterSpacing: "-0.5px"
  },
  whyDesc: {
    color: "#64748b",
    marginBottom: "40px",
    lineHeight: "1.6",
    fontSize: "15px"
  },
  timelineStack: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    borderLeft: "2px solid #e2e8f0",
    paddingLeft: "24px",
    gap: "30px",
    marginLeft: "8px"
  },
  timelineItem: {
    display: "flex",
    position: "relative"
  },
  timelineNode: {
    position: "absolute",
    left: "-33px",
    top: "4px",
    background: "#ffffff",
    padding: "4px 0"
  },
  nodeDotInside: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "#cbd5e1",
    transition: "all 0.25s ease"
  },
  timelineTextPane: {
    flex: 1
  },
  featureItemTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0b0f19",
    margin: "0 0 4px 0"
  },
  featureItemDesc: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    lineHeight: "1.5"
  },
  ctaWrapper: {
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "80px 40px"
  },
  ctaCleanBar: {
    display: "flex",
    gap: "30px",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "40px 0",
    borderTop: "1px solid #e2e8f0"
  },
  icon: {
    fontSize: "10px",
    fontWeight: "900",
    letterSpacing: "2px",
    background: "#d4af37",
    color: "#fff",
    padding: "6px 14px",
    borderRadius: "3px"
  },
  ctaText: {
    marginBottom: "16px",
    lineHeight: "1.6",
    color: "#334155",
    fontSize: "16px"
  },
  link: {
    color: "#0b0f19",
    fontWeight: "700",
    textDecoration: "underline",
    textDecorationColor: "#d4af37",
    textUnderlineOffset: "4px"
  },
  badges: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap"
  },
  badgeGold: {
    background: "#fdfbf2",
    border: "1px solid #f6e6b4",
    padding: "6px 14px",
    borderRadius: "4px",
    fontSize: "13px",
    color: "#846200",
    fontWeight: "600"
  },
  badgeDark: {
    background: "#0b0f19",
    padding: "6px 14px",
    borderRadius: "4px",
    fontSize: "13px",
    color: "#ffffff",
    fontWeight: "500"
  },
  buttons: {
    marginTop: "50px",
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  backBtn: {
    padding: "16px 36px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "700",
    color: "#475569",
    fontSize: "15px"
  },
  mainBtn: {
    padding: "16px 36px",
    borderRadius: "8px",
    border: "none",
    background: "#0b0f19",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "15px",
    boxShadow: "0 10px 20px -5px rgba(11, 15, 25, 0.15)"
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(11, 15, 25, 0.4)",
    backdropFilter: "blur(8px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
    padding: "20px",
    boxSizing: "border-box"
  },
  quoteModal: {
    width: "100%",
    maxWidth: "500px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "12px",
    padding: "45px",
    position: "relative",
    boxSizing: "border-box",
    boxShadow: "0 40px 80px -20px rgba(0, 0, 0, 0.2)"
  },
  closeBtn: {
    position: "absolute",
    top: "16px",
    right: "16px",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    border: "none",
    background: "#f1f5f9",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10
  },
  stepBadge: {
    color: "#d4af37",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "1.5px"
  },
  modalTitle: {
    marginTop: "6px",
    marginBottom: "16px",
    fontSize: "24px",
    fontWeight: "900",
    color: "#0b0f19",
    letterSpacing: "-0.5px"
  },
  progressBar: {
    width: "100%",
    height: "3px",
    background: "#f1f5f9",
    borderRadius: "10px",
    overflow: "hidden",
    marginBottom: "28px"
  },
  progressFill: {
    height: "100%",
    background: "#d4af37",
    transition: "0.4s cubic-bezier(0.16, 1, 0.3, 1)"
  },
  question: {
    fontSize: "17px",
    marginBottom: "20px",
    color: "#0b0f19",
    lineHeight: "1.4",
    fontWeight: "800"
  },
  modalOptionStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0"
  },
  modalRowOption: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #f1f5f9",
    background: "#fff",
    padding: "16px 8px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    cursor: "pointer",
    textAlign: "left",
    boxSizing: "border-box"
  },
  rowArrow: {
    fontSize: "14px",
    color: "#cbd5e1",
    opacity: 0.5,
    transition: "all 0.25s ease"
  },
  optionText: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#334155"
  },
  backLink: {
    marginTop: "20px",
    border: "none",
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    fontSize: "13.5px",
    fontWeight: "600",
    padding: "0"
  },
  modalInput: {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "4px",
    border: "1px solid #cbd5e1",
    marginBottom: "14px",
    fontSize: "14.5px",
    outline: "none",
    boxSizing: "border-box",
    background: "#f8fafc",
    transition: "all 0.2s ease"
  },
  footerBtns: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "15px",
    marginTop: "10px"
  },
  whatsappBtn: {
    background: "#10b981",
    color: "#fff",
    border: "none",
    padding: "13px 24px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14.5px",
    fontWeight: "700"
  }
};